/**
 * ConnectCalendarButton Component
 * Dropdown button for connecting Google or Microsoft calendars.
 */

import { useState, useRef, useEffect } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { CalendarProvider } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ConnectCalendarButtonProps {
  onConnect: (provider: CalendarProvider) => void;
  disabled?: boolean;
}

// Provider icons as inline SVGs for simplicity
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

export function ConnectCalendarButton({ onConnect, disabled }: ConnectCalendarButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (provider: CalendarProvider) => {
    setIsOpen(false);
    onConnect(provider);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Connect Calendar
        <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md border bg-popover shadow-lg z-50">
          <div className="py-1">
            <button
              onClick={() => handleSelect(CalendarProvider.Google)}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors"
            >
              <GoogleIcon className="h-5 w-5" />
              <span>Google Calendar</span>
            </button>
            <button
              onClick={() => handleSelect(CalendarProvider.Microsoft)}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors"
            >
              <MicrosoftIcon className="h-5 w-5" />
              <span>Microsoft Outlook</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectCalendarButton;
